// Which picture a food shows (staple pictures plan, `plans/staple-food-pictures.md`, Part B step 11).
//
// One synchronous answer, used by every screen that shows a food: the typing suggestions, the Add
// Foods cards and the food details page. Synchronous because Angular templates call it while
// rendering a row, and a suggestion row carries only a staple id.
//
// The order is the whole rule, and it is the same everywhere:
//   1. the food's OWN photo wins — a staple picture never covers a picture the user took;
//   2. otherwise the staple picture named by the installed snapshot;
//   3. otherwise the name on the loaded document, for the case the snapshot cannot answer;
//   4. otherwise nothing, and the screen looks exactly as it did before this feature.
//
// See design-docs/food-staples.md.

import { STAPLE_PICTURE_FILES, StaplePictureFiles } from './staple-picture-files.generated';

/**
 * As much of a food as this lookup reads. Deliberately not `Food`: the details page holds a whole
 * document, while a card may hold a header, and both answer the same question.
 */
export interface FoodWithMaybePicture {
  _id?: string;
  /** the user's own photo, if they took one */
  image?: { sizes?: unknown[] } | null;
  staple?: { picture?: string } | null;
}

/** what the lookup needs from the rest of the app */
export interface StaplePictureDeps {
  /**
   * The picture name the installed staple snapshot has for an id, or `undefined`. This is asked
   * FIRST, before the loaded document's own field, because a food copied into the user's database
   * keeps whatever picture name it had at the time — so a picture later renamed or split would go on
   * showing the old one.
   */
  pictureNameForId: (foodId: string) => string | undefined;
}

/** whether the user took their own photo of this food — the same test both pages already use */
export function hasOwnPhoto(food: FoodWithMaybePicture | undefined): boolean {
  const sizes = food?.image?.sizes;
  return Array.isArray(sizes) && sizes.length > 0;
}

/**
 * The files to show for a food, or `undefined` for "no picture — look exactly as before".
 *
 * `food` may be a whole document, a header, or a bare staple id (a suggestion row has nothing else).
 * Anything unexpected in a document — a `staple.picture` that is not a string, a name this build
 * ships no file for — reads as no picture rather than being trusted, which is also what makes an
 * upload and a release safe to land in either order.
 */
export function staplePictureFor(
  food: FoodWithMaybePicture | string | undefined,
  deps: StaplePictureDeps,
): StaplePictureFiles | undefined {
  const document = typeof food === 'string' ? undefined : food;
  // the user's own photo wins over everything, including a staple picture
  if (hasOwnPhoto(document)) {
    return undefined;
  }
  const foodId = typeof food === 'string' ? food : document?._id;
  const fromSnapshot = typeof foodId === 'string' && foodId !== '' ? deps.pictureNameForId(foodId) : undefined;
  const name = fromSnapshot ?? document?.staple?.picture;
  if (typeof name !== 'string' || name === '') {
    return undefined;
  }
  // an OWN key only: the table is a plain object literal, so a name like `constructor` would
  // otherwise return a value off `Object.prototype` and be shown as if it were a picture
  return Object.prototype.hasOwnProperty.call(STAPLE_PICTURE_FILES, name) ? STAPLE_PICTURE_FILES[name] : undefined;
}
